import { BoxLeadEntity } from "@modules/boxLead/entities/boxLeadEntity"
import { useBoxLeads } from "@modules/boxLead/hooks/useBoxLead"
import { useBoxLeadDetailsSidebar } from "@modules/boxLead/hooks/useBoxLeadDetailsSidebar"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { UserCell } from "@modules/members/components/userCell"
import { DataTable, renderTableColumns } from "@modules/ui/components/dataTables/datatable"
import { HuNote } from "@modules/ui/components/huNote"
import { HUText } from "@modules/ui/components/huText"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { Column, ColumnProps } from "primereact/column"
import { DataTableStateEvent, DataTableValue } from "primereact/datatable"
import { Skeleton } from "primereact/skeleton"
import { useEffect, useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"

type LeadColumnKey = "name" | "type" | "firstContact" | "note"

type Props = {
  diplayedColumns?: LeadColumnKey[]
  headerRightView?: React.JSX.Element
  columns?: ColumnProps[]
  page: number
  setPage: (p: number) => void
  size: number
  setSize: (s: number) => void
  hideHeaderOnEmptyLoading?: boolean
  empty: {
    full?: boolean
    view: React.ReactNode
  }
  setCountTab: (count: number) => void
  nameFilter: string
} & React.HTMLAttributes<HTMLDivElement>

export const DEFAULT_PAGE_SIZE = 20

export const BoxLeadTable: React.FC<Props> = ({
  diplayedColumns,
  columns,
  page,
  setPage,
  size,
  setSize,
  empty,
  nameFilter,
  setCountTab,
}) => {
  const displayedColumnsDefault: LeadColumnKey[] = ["name", "type", "firstContact", "note"]
  const theme = useTheme()
  const language = useLanguage()
  const [selectedLead, setselectedLead] = useState<DataTableValue>()
  const { navigateToLeadDetailsInfo } = useBoxLeadDetailsSidebar()
  const { data, isLoading } = useBoxLeads(page, size, nameFilter)

  useEffect(() => {
    if (data && data.totalSize) {
      setCountTab(data.totalSize)
    } else setCountTab(0)
  }, [data])

  const headerColumn = (key: LeadColumnKey) => {
    switch (key) {
      case "name":
        return word("global.table.headerColumn.name")
      case "type":
        return word("global.table.headerColumn.type")
      case "firstContact":
        return word("global.table.headerColumn.firstContact")
      case "note":
        return word("global.table.headerColumn.note")
      default:
        return ""
    }
  }

  const valueColumn = (key: LeadColumnKey, lead: BoxLeadEntity) => {
    switch (key) {
      case "name":
        return (
          <UserCell
            member={{
              avatar: lead.user?.avatar,
              firstname: lead.user?.firstname ?? lead.firstname ?? "",
              lastname: lead.user?.lastname ?? lead.lastname ?? "",
            }}
            subtitle={lead.getUserName()}
            subtitleColor={theme.neutralColor500}
          />
        )
      case "type":
        return (
          <HUText color={theme.mainWhite} fontStyle="TS">
            {lead.getWordingType()}
          </HUText>
        )
      case "firstContact":
        return (
          <HUText color={theme.mainWhite} fontStyle="BM">
            {formatDateWithHyphen(dayjs(lead.firstContact).format("L"))}
          </HUText>
        )

      case "note":
        return <HuNote id={lead.id} index={lead.id} note={lead.note ?? ""} />
    }
  }

  const onPage = (event: DataTableStateEvent) => {
    setPage(event.page ?? 0)
    setSize(event.rows)
  }

  const onSelectedLead = (e: BoxLeadEntity) => {
    navigateToLeadDetailsInfo(e.id as string, {
      onHide: () => {
        setselectedLead(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setselectedLead(e)
  }

  // SKELETON
  if (isLoading) {
    return (
      <div>
        <Skeleton width="100%" height="45rem" className="rounded-33" />
      </div>
    )
  }

  return (
    <div className="flex flex-column px-4">
      {!data || data.content.length <= 0 ? (
        empty.view
      ) : (
        <DataTable
          value={data.content as DataTableValue[]}
          dataKey="id"
          paginator
          lazy
          onPage={onPage}
          first={size * page}
          totalRecords={data.totalSize}
          rows={size}
          currentPageReportTemplate={""}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage={empty.view}
          loading={isLoading}
          selectionMode="single"
          selection={selectedLead}
          onSelectionChange={(e) => onSelectedLead(e.value as BoxLeadEntity)}
          unstyled={undefined}
          metaKeySelection={true}
        >
          {renderTableColumns(
            diplayedColumns ?? displayedColumnsDefault,
            headerColumn,
            valueColumn,
            theme as DefaultTheme,
            language,
          )}
          {columns?.map((columnProps, index) => <Column key={"more-column-" + index} {...columnProps} />)}
        </DataTable>
      )}
    </div>
  )
}
